import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { itemState, money } from './utils';

export default function CreateProjectDialog({ open, onOpenChange, selected, properties }) {
  const navigate = useNavigate();
  const [cpForm, setCpForm] = useState(null);
  const [cpBusy, setCpBusy] = useState(false);

  useEffect(() => {
    if (!open || !selected) return;
    let cancelled = false;
    (async () => {
      let suggested = '';
      try {
        const pr = await api.get('/projects');
        const yy = new Date().toISOString().slice(2, 4);
        let max = 0;
        (pr.data || []).forEach(p => {
          const m = (p.project_number || '').match(new RegExp(`^MCC ${yy}-(\\d+)`));
          if (m) max = Math.max(max, parseInt(m[1], 10));
        });
        suggested = `MCC ${yy}-${String(max + 1).padStart(3, '0')}`;
      } catch { /* leave blank -> backend generates */ }
      if (cancelled) return;
      const sel = {};
      (selected.line_items || []).forEach(it => { if (itemState(it) !== 'excluded') sel[it.line_item_id] = true; });
      setCpForm({
        name: selected.title || '',
        project_number: suggested,
        status: 'Draft',
        property_id: selected.property_id || '',
        checklistSel: sel,
      });
    })();
    return () => { cancelled = true; };
  }, [open, selected]);

  const checklistPreview = selected ? (selected.line_items || []).filter(it => itemState(it) !== 'excluded') : [];

  const confirmCreateProject = async () => {
    if (!cpForm.name.trim()) return toast.error('Project name is required');
    setCpBusy(true);
    try {
      const checklist_items = (selected.line_items || [])
        .filter(it => itemState(it) !== 'excluded' && cpForm.checklistSel[it.line_item_id])
        .map(it => ({ activity: it.description, description: it.scope_detail, scope_detail: it.scope_detail, fee: Number(it.fee) || 0 }));
      const r = await api.post(`/proposals/${selected.id}/create-project`, {
        name: cpForm.name.trim(),
        project_number: cpForm.project_number.trim() || undefined,
        status: cpForm.status,
        property_id: cpForm.property_id || null,
        checklist_items,
      });
      toast.success('Project created');
      if (r.data.created_property) {
        toast.info(`Property "${r.data.created_property.address}" was created from the proposal address and linked to this project.`);
      }
      onOpenChange(false);
      navigate(`/projects/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create project'); }
    setCpBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: '#010A21' }}>Create Project from Proposal</DialogTitle>
          <DialogDescription>Confirm project details and initialize the compliance checklist.</DialogDescription>
        </DialogHeader>
        {cpForm && (
          <div className="space-y-5">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#031333' }}>Project Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs text-muted-foreground">Project Name</Label><Input value={cpForm.name} onChange={e => setCpForm({ ...cpForm, name: e.target.value })} className="bg-background border-border mt-1" data-testid="cp-name" /></div>
                <div><Label className="text-xs text-muted-foreground">Project Number</Label><Input value={cpForm.project_number} onChange={e => setCpForm({ ...cpForm, project_number: e.target.value })} className="bg-background border-border mt-1" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="cp-number" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select value={cpForm.status} onValueChange={v => setCpForm({ ...cpForm, status: v })}>
                    <SelectTrigger className="bg-background border-border mt-1" data-testid="cp-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{['Draft', 'Not Started', 'In Progress', 'On Hold', 'Completed'].map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Property</Label>
                  <div className="mt-1"><SearchableSelect options={(Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}${p.borough ? ` : ${p.borough}` : ''}` }))} value={cpForm.property_id} onChange={v => setCpForm({ ...cpForm, property_id: v })} placeholder="Select property (optional)" searchPlaceholder="Search..." emptyText="No properties." testid="cp-property" /></div>
                  {!cpForm.property_id && (selected?.property_address || selected?.project_address) && (
                    <p className="text-xs mt-1" style={{ color: '#031333' }} data-testid="cp-auto-property-note">A property will be auto-created from the proposal address ("{selected.property_address || selected.project_address}") and linked to this project.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#031333' }}>Initialize Compliance Checklist</p>
              <p className="text-xs text-muted-foreground">These items will be added to the project's compliance checklist. Uncheck any you want to exclude.</p>
              <div className="border border-border rounded-md divide-y divide-border">
                {checklistPreview.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">No included or conditional line items to add.</p>
                ) : checklistPreview.map(it => (
                  <label key={it.line_item_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/20" data-testid={`cp-checklist-${it.line_item_id}`}>
                    <Checkbox checked={!!cpForm.checklistSel[it.line_item_id]} onCheckedChange={v => setCpForm({ ...cpForm, checklistSel: { ...cpForm.checklistSel, [it.line_item_id]: !!v } })} data-testid={`cp-checklist-cb-${it.line_item_id}`} />
                    <span className="flex-1 text-sm" style={{ color: '#010A21' }}>{it.description || '(untitled)'}{itemState(it) === 'conditional' && <span className="text-xs ml-2" style={{ color: '#D97706' }}>conditional</span>}</span>
                    <span className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: '#031333' }}>{money(it.fee)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="border-border" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-primary text-primary-foreground" disabled={cpBusy || !cpForm} onClick={confirmCreateProject} data-testid="cp-confirm">
            {cpBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
